import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useFundContext from '@/contexts/Fund/useFundContext';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog/ConfirmationDialog';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { PaymentDeviceRentalRequestStatus, PaymentDeviceRentalStatus } from '@/models/common/PaymentDeviceEnums';
import { FundPaymentDevicesDtoIn, PaymentDeviceRentalDtoIn, PaymentDeviceRentalRequestDtoIn } from '@/models/in/PaymentDeviceDtoIn';
import { formatDeviceDate, formatPickupDay, fundDevicesUrl, isAwaitingPickup, paymentDeviceErrorMessage } from '@/helpers/paymentDevices';
import { accessoriesText } from '@/helpers/paymentDeviceAccessories';
import { formatFeeAmount } from '@/helpers/paymentDeviceFees';
import usePaymentDeviceApi from '@/pages/Management/PaymentDevices/usePaymentDeviceApi';
import RentalRequestDialog from './RentalRequestDialog';
import RentalFeeSummary from './RentalFeeSummary';
import { Pill, Tone } from './Pill';

const requestTone: Record<PaymentDeviceRentalRequestStatus, Tone> = {
  [PaymentDeviceRentalRequestStatus.Pending]: 'neutral',
  [PaymentDeviceRentalRequestStatus.Approved]: 'success',
  [PaymentDeviceRentalRequestStatus.Rejected]: 'danger',
  [PaymentDeviceRentalRequestStatus.Cancelled]: 'neutral',
};

const DeviceIcon = ({ size = 22 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="5" y="2.5" width="14" height="19" rx="2.5" />
    <rect x="8" y="5.5" width="8" height="5" rx="1" />
    <path d="M8.5 14h.01M12 14h.01M15.5 14h.01M8.5 17.5h.01M12 17.5h.01M15.5 17.5h.01" />
  </svg>
);

const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const NoticeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" className="flex-none mt-px">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5M12 8h.01" />
  </svg>
);

const NewRequestButton = ({ onClick, large = false }: { onClick: () => void; large?: boolean }) => {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-lg bg-(--surface-primary) text-white font-semibold whitespace-nowrap cursor-pointer hover:bg-(--surface-primary-dark) ${large ? 'h-12 px-7 text-base' : 'h-11 px-5 text-[15px]'}`}
    >
      <PlusIcon />
      {t('paymentDevices.coordinator.newRequest')}
    </button>
  );
};

const STEPS = [1, 2, 3, 4] as const;

const Field = ({ label, children, danger = false }: { label: string; children: ReactNode; danger?: boolean }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-xs text-(--font-body-3)">{label}</span>
    <span className={`text-[15px] ${danger ? 'font-semibold text-[#a12626]' : 'font-medium'}`}>{children}</span>
  </div>
);

/** A terminal the fund holds now: reserved and waiting at the office, or with the fund. */
const DeviceCard = ({ rental }: { rental: PaymentDeviceRentalDtoIn }) => {
  const { t } = useTranslation();
  const isReserved = rental.status === PaymentDeviceRentalStatus.Reserved;

  return (
    <article className="flex flex-col gap-4 rounded-xl border border-(--border-main) bg-white p-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="flex-none w-11 h-11 rounded-[10px] bg-(--surface-primary-lowest) text-(--font-primary) flex items-center justify-center">
            <DeviceIcon />
          </span>
          <div className="flex flex-col gap-0.5">
            <span className="text-xs text-(--font-body-3)">{t('paymentDevices.coordinator.device')}</span>
            <span dir="ltr" className="text-lg font-bold text-right">{rental.deviceNumber}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {rental.isOverdue && <Pill tone="danger" dot={false}>{t('paymentDevices.overdue')}</Pill>}
          {isReserved
            ? <Pill tone="warning">{t('paymentDevices.coordinator.waitingForPickup')}</Pill>
            : <Pill tone="info">{t('paymentDevices.coordinator.withYou')}</Pill>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 pt-3.5 border-t border-(--border-divider)">
        {isReserved
          ? <Field label={t('paymentDevices.coordinator.fromDate')}>{formatDeviceDate(rental.startDate)}</Field>
          : <Field label={t('paymentDevices.coordinator.handedOverOn')}>{formatDeviceDate(rental.handedOverDate)}</Field>}
        <Field label={t('paymentDevices.coordinator.expectedReturn')} danger={rental.isOverdue}>
          {formatDeviceDate(rental.expectedReturnDate)}
        </Field>
      </div>

      {(rental.handedOverAccessories?.length ?? 0) > 0 && (
        <span className="text-sm text-(--font-body-2)">
          {t('paymentDevices.accessories.coordinatorLine', { items: accessoriesText(rental.handedOverAccessories, t) })}
        </span>
      )}

      {rental.fee && <RentalFeeSummary fee={rental.fee} audience="coordinator" />}

      {isReserved && (
        <div className="flex items-start gap-2.5 rounded-lg bg-(--surface-tertiary-low) text-[#09474e] px-3.5 py-3 text-sm leading-relaxed">
          <NoticeIcon />
          <span>{t('paymentDevices.coordinator.reservedNotice')}</span>
        </div>
      )}
      {rental.isOverdue && (
        <div className="flex items-start gap-2.5 rounded-lg bg-[#fdecec] text-[#7a1d1d] px-3.5 py-3 text-sm leading-relaxed">
          <NoticeIcon />
          <span>{t('paymentDevices.coordinator.overdueNotice')}</span>
        </div>
      )}
    </article>
  );
};

const REQUEST_COLUMNS = 'grid grid-cols-[80px_minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.4fr)_120px] gap-3 items-center px-5';

/**
 * «מכשירי סליקה» for a coordinator (HAT-1434): the terminals the fund holds, its rental requests
 * and their outcome, and the form to ask the office for a terminal. The four steps are explained
 * only while the fund has nothing yet - no device and no request - as one empty state that offers
 * the request; once it has, the page shows just its devices and requests.
 */
const FundPaymentDevicesPage = () => {
  const { t } = useTranslation();
  const { currentFund } = useFundContext();
  const fundId = currentFund?.id;
  const api = usePaymentDeviceApi();

  const { data, isLoading } = useGetAuthApi<FundPaymentDevicesDtoIn>(fundId ? fundDevicesUrl(fundId) : '');

  const [isFormOpen, setIsFormOpen] = useState(false);
  // After a request is sent: where and when to collect the device, to acknowledge (HAT-1434).
  const [isSubmittedOpen, setIsSubmittedOpen] = useState(false);
  const [toCancel, setToCancel] = useState<PaymentDeviceRentalRequestDtoIn | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);

  if (!fundId) return null;

  const current = data?.current ?? [];
  const requests = data?.requests ?? [];
  // Only rentals that cost something are worth listing once returned: their fee is the reason to look.
  // Only rentals that actually cost something: one returned before the fees started has a fee block
  // of zero, and listing it would only be noise.
  const pastWithFee = (data?.history ?? []).filter((rental) =>
    (rental.fee?.accruedAmount ?? 0) > 0 || (rental.fee?.charges.length ?? 0) > 0 || (rental.missingAccessories?.length ?? 0) > 0);
  const feeRate = data?.feesEnabled && data.feeRatePerDay ? data.feeRatePerDay : null;
  const hasPendingRequest = requests.some((r) => r.status === PaymentDeviceRentalRequestStatus.Pending);
  const isEmpty = !isLoading && current.length === 0 && requests.length === 0;
  const openForm = () => setIsFormOpen(true);

  const confirmCancel = async () => {
    if (!toCancel) return;
    setCancelError(null);
    setIsCancelling(true);
    try {
      await api.post<PaymentDeviceRentalRequestDtoIn, object>(`${fundDevicesUrl(fundId)}/requests/${toCancel.id}/cancel`, {});
      setToCancel(null);
    } catch (err) {
      setCancelError(paymentDeviceErrorMessage(err, t, 'paymentDevices.errors.save'));
    } finally {
      setIsCancelling(false);
    }
  };

  const requestOutcome = (request: PaymentDeviceRentalRequestDtoIn) => {
    if (request.status === PaymentDeviceRentalRequestStatus.Approved && request.rental) {
      return t('paymentDevices.coordinator.approvedDevice', { number: request.rental.deviceNumber });
    }
    if (isAwaitingPickup(request) && request.holdUntil) {
      return t('paymentDevices.coordinator.readyForPickup', { day: formatPickupDay(request.holdUntil) });
    }
    if (request.expiredAt) {
      return t('paymentDevices.coordinator.notCollected', { day: formatPickupDay(request.holdUntil) });
    }
    if (request.status === PaymentDeviceRentalRequestStatus.Rejected) {
      return [
        request.rejectionReason ? t(`paymentDevices.rejectionReason.${request.rejectionReason}`) : null,
        request.reviewNote,
      ].filter(Boolean).join(' · ') || null;
    }
    return null;
  };

  return (
    <div className="flex flex-col gap-7 w-full">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div className="flex flex-col gap-1.5">
          <h1 className="m-0 text-[28px] font-bold text-(--font-body-1)">{t('paymentDevices.coordinator.title')}</h1>
          <p className="m-0 text-[15px] leading-normal text-(--font-body-2) max-w-[720px]">
            {isEmpty ? t('paymentDevices.coordinator.introShort') : t('paymentDevices.coordinator.intro')}
          </p>
          {feeRate != null && (
            <p className="m-0 text-sm text-(--font-body-3)">{t('paymentDevices.fees.rateLine', { rate: formatFeeAmount(feeRate) })}</p>
          )}
        </div>
        {!isEmpty && <NewRequestButton onClick={openForm} />}
      </div>

      {isLoading ? (
        <p className="body-md-regular text-(--font-body-3)">{t('loading')}...</p>
      ) : isEmpty ? (
        <div className="flex flex-col items-center justify-center gap-7 rounded-2xl border border-dashed border-(--border-low) bg-(--surface-grey-lowest) px-6 py-12">
          <span className="w-[88px] h-[88px] rounded-full bg-(--surface-primary-lowest) text-(--font-primary) flex items-center justify-center">
            <DeviceIcon size={44} />
          </span>
          <div className="flex flex-col items-center gap-2 text-center">
            <h2 className="m-0 text-[22px] font-bold">{t('paymentDevices.coordinator.emptyTitle')}</h2>
            <p className="m-0 text-[15px] leading-relaxed text-(--font-body-2) max-w-[460px]">{t('paymentDevices.coordinator.emptyText')}</p>
          </div>
          <ol className="list-none m-0 p-0 grid grid-cols-2 md:grid-cols-4 gap-4 w-full max-w-[860px]">
            {STEPS.map((step) => (
              <li key={step} className="flex flex-col items-center gap-2.5 text-center px-3 py-4 bg-white border border-(--border-divider) rounded-xl">
                <span className="w-8 h-8 rounded-full bg-(--surface-primary) text-white flex items-center justify-center text-sm font-bold">{step}</span>
                <span className="text-[15px] font-semibold">{t(`paymentDevices.coordinator.steps.${step}.title`)}</span>
                <span className="text-[13px] leading-snug text-(--font-body-2)">{t(`paymentDevices.coordinator.steps.${step}.text`)}</span>
              </li>
            ))}
          </ol>
          <NewRequestButton onClick={openForm} large />
        </div>
      ) : (
        <>
          <section className="flex flex-col gap-3.5">
            <div className="flex items-baseline gap-2.5">
              <h2 className="m-0 text-[19px] font-bold">{t('paymentDevices.coordinator.currentDevices')}</h2>
              {current.length > 0 && <span className="text-sm text-(--font-body-3)">{t('paymentDevices.coordinator.devicesCount', { count: current.length })}</span>}
            </div>
            {current.length === 0 ? (
              <p className="m-0 rounded-xl border border-(--border-divider) px-5 py-4 text-[15px] text-(--font-body-2)">{t('paymentDevices.coordinator.noCurrentDevices')}</p>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {current.map((rental) => <DeviceCard key={rental.id} rental={rental} />)}
              </div>
            )}
          </section>

          {pastWithFee.length > 0 && (
            <section className="flex flex-col gap-3.5">
              <h2 className="m-0 text-[19px] font-bold">{t('paymentDevices.fees.pastRentals')}</h2>
              <div className="flex flex-col gap-3">
                {pastWithFee.map((rental) => (
                  <article key={rental.id} className="flex flex-col gap-2 rounded-xl border border-(--border-divider) bg-white px-5 py-4">
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <span className="font-semibold">
                        {t('paymentDevices.coordinator.device')} <bdi>{rental.deviceNumber}</bdi>
                      </span>
                      <span className="text-sm text-(--font-body-2)">
                        {formatDeviceDate(rental.handedOverDate)} – {formatDeviceDate(rental.returnedDate)}
                      </span>
                      {(rental.missingAccessories?.length ?? 0) > 0 && (
                        <Pill tone="danger" dot={false}>
                          {t('paymentDevices.accessories.missingTag', { items: accessoriesText(rental.missingAccessories, t) })}
                        </Pill>
                      )}
                    </div>
                    {rental.fee && <RentalFeeSummary fee={rental.fee} compact audience="coordinator" />}
                  </article>
                ))}
              </div>
            </section>
          )}

          <section className="flex flex-col gap-3.5">
            <h2 className="m-0 text-[19px] font-bold">{t('paymentDevices.coordinator.requests')}</h2>
            {requests.length === 0 ? (
              <p className="m-0 rounded-xl border border-(--border-divider) px-5 py-4 text-[15px] text-(--font-body-2)">{t('paymentDevices.coordinator.noRequests')}</p>
            ) : (
              <div className="rounded-xl border border-(--border-main) overflow-x-auto">
                <div className="min-w-[760px]">
                  <div className={`${REQUEST_COLUMNS} py-3 bg-(--surface-grey-low) text-[13px] font-semibold text-(--font-body-2)`}>
                    <span>{t('paymentDevices.coordinator.columns.id')}</span>
                    <span>{t('paymentDevices.coordinator.columns.period')}</span>
                    <span>{t('paymentDevices.coordinator.columns.area')}</span>
                    <span>{t('paymentDevices.coordinator.columns.submitted')}</span>
                    <span>{t('paymentDevices.coordinator.columns.status')}</span>
                    <span />
                  </div>
                  {requests.map((request) => {
                    const outcome = requestOutcome(request);
                    return (
                      <div key={request.id} className={`${REQUEST_COLUMNS} py-3.5 border-t border-(--border-divider) text-[15px]`}>
                        <span className="font-semibold">{request.id}</span>
                        <span>{formatDeviceDate(request.startDate)} – {formatDeviceDate(request.expectedReturnDate)}</span>
                        <span>{t(`paymentDevices.pickupArea.${request.pickupArea}`)}</span>
                        <span className="text-(--font-body-2)">{new Date(request.submittedAt).toLocaleDateString('he-IL')}</span>
                        <span className="flex flex-col items-start gap-1">
                          <Pill tone={requestTone[request.status]}>{t(`paymentDevices.coordinator.requestStatus.${request.status}`)}</Pill>
                          {outcome && <span className="text-[13px] text-(--font-body-2)">{outcome}</span>}
                        </span>
                        <span className="text-left">
                          {request.status === PaymentDeviceRentalRequestStatus.Pending && (
                            <button
                              type="button"
                              onClick={() => { setCancelError(null); setToCancel(request); }}
                              className="h-9 px-3.5 rounded-lg border border-(--border-main) bg-white text-sm cursor-pointer hover:bg-(--surface-hover) whitespace-nowrap"
                            >
                              {t('paymentDevices.coordinator.cancelRequest')}
                            </button>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </section>
        </>
      )}

      <RentalRequestDialog
        open={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmitted={() => setIsSubmittedOpen(true)}
        fundId={fundId}
        fundName={currentFund?.name}
        hasPendingRequest={hasPendingRequest}
        feeRatePerDay={feeRate}
      />

      <ConfirmationDialog
        open={isSubmittedOpen}
        onOpenChange={setIsSubmittedOpen}
        title={t('paymentDevices.coordinator.submitted.title')}
        message={t('paymentDevices.coordinator.pickupNotice')}
        confirmText="paymentDevices.coordinator.submitted.ok"
        hideCancel
        onConfirm={() => setIsSubmittedOpen(false)}
      />

      <ConfirmationDialog
        open={toCancel !== null}
        onOpenChange={(isOpen) => { if (!isOpen) setToCancel(null); }}
        title={t('paymentDevices.coordinator.cancelConfirmTitle')}
        message={[t('paymentDevices.coordinator.cancelConfirm', { id: toCancel?.id ?? '' }), cancelError].filter(Boolean).join('\n\n')}
        confirmText="paymentDevices.coordinator.cancelConfirmButton"
        cancelText="paymentDevices.coordinator.keepRequest"
        variant="danger"
        isLoading={isCancelling}
        onConfirm={confirmCancel}
      />
    </div>
  );
};

export default FundPaymentDevicesPage;
